import { Component, inject, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { authBackendEndPointToken } from '../app.config';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ErrorService } from '../shared/error.service';
import { SuperAdminDetailsComponent } from './superadmin-details.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogData } from '../practitioner-list/confirm-action-dialog.component';

export type SystemAdminType = 'SuperAdmin' | 'EventManager';

export interface SystemAdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber?: string;
  isApproved: boolean;
  createdAt: string;
  lastLoginAt?: string;
  status: 'Active' | 'Deactivated';
  accountType: SystemAdminType; // NEW: tags which flow this record belongs to
}

@Component({
  selector: 'app-superadmin-list',
  standalone: true,
  imports: [
    RouterModule, CommonModule, MatCardModule, MatButtonModule,
    MatIconModule, MatTableModule, MatDividerModule, EmptyStateComponent,
    MatSortModule, MatMenuModule, MatTabsModule, MatProgressSpinnerModule,
    MatChipsModule
  ],
  templateUrl: './superadmin-list.component.html',
  styleUrl: './superadmin-list.component.scss'
})
export class SuperadminListComponent {
  currentSegment: 'active' | 'deactivated' = 'active';

  immutableTableData: SystemAdminUser[] = [];
  tableDataSource = new MatTableDataSource<SystemAdminUser>();
  tableColumns: string[] = ['name', 'accountType', 'email', 'phoneNumber', 'lastLogin', 'createdDate', 'status', 'action'];

  pageTitle = 'Active System Administrators';
  pageSubtitle = 'Manage SuperAdmins and Global Event Managers';
  isLoading = false;

  currentSort: Sort = { active: 'createdDate', direction: 'desc' };

  http = inject(HttpClient);
  router = inject(Router);
  dialog = inject(MatDialog);
  _sn = inject(MatSnackBar);
  backendApi = inject(authBackendEndPointToken);
  errorService = inject(ErrorService);
  cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.setupSorting();
    this.loadAllUsers();
  }

  onSortChange(sort: Sort) {
    this.currentSort = sort;
    this.sortData(sort);
  }

  private setupSorting() {
    this.tableDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name': return (item.fullName || '').toLowerCase();
        case 'accountType': return item.accountType;
        case 'email': return (item.email || '').toLowerCase();
        case 'phoneNumber': return (item.phoneNumber || '').toLowerCase();
        case 'lastLogin':
          if (!item.lastLoginAt) return 0;
          const t1 = this.parseDate(item.lastLoginAt).getTime();
          return isNaN(t1) ? 0 : t1;
        case 'createdDate':
          if (!item.createdAt) return 0;
          const t2 = this.parseDate(item.createdAt).getTime();
          return isNaN(t2) ? 0 : t2;
        case 'status': return (item.status || '').toLowerCase();
        default: return (item as any)[property] || '';
      }
    };
  }

  private parseDate(dateString: string): Date {
    const truncatedDate = dateString.replace(/(\.\d{3})\d+/, '$1');
    return new Date(truncatedDate);
  }

  private sortData(sort: Sort) {
    const data = this.tableDataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.tableDataSource.data = data;
      return;
    }
    const sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      const valueA = this.tableDataSource.sortingDataAccessor(a, sort.active);
      const valueB = this.tableDataSource.sortingDataAccessor(b, sort.active);
      return this.compare(valueA, valueB, isAsc);
    });
    this.tableDataSource.data = sortedData;
  }

  private compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private loadAllUsers() {
    this.isLoading = true;

    forkJoin({
      superAdmins: this.http.get<any[]>(`${this.backendApi}/api/SuperAdmin/users`),
      eventManagers: this.http.get<any[]>(`${this.backendApi}/api/Admin/global-event-managers/list`)
    }).subscribe({
      next: ({ superAdmins, eventManagers }) => {
        const tagged: SystemAdminUser[] = [
          ...superAdmins.map(u => ({ ...u, accountType: 'SuperAdmin' as const })),
          ...eventManagers.map(u => ({ ...u, accountType: 'EventManager' as const }))
        ];

        this.immutableTableData = tagged;
        this.filterBySegment(this.currentSegment);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load system admin users:', error);
        this.errorService.openandCloseError('Failed to load users');
        this.isLoading = false;
      }
    });
  }

  onTabChange(index: number) {
    const segments: Array<'active' | 'deactivated'> = ['active', 'deactivated'];
    this.currentSegment = segments[index];
    this.filterBySegment(this.currentSegment);
  }

  private filterBySegment(segment: 'active' | 'deactivated') {
    let filtered: SystemAdminUser[] = [];

    if (segment === 'active') {
      filtered = this.immutableTableData.filter(u => u.isApproved);
      this.pageTitle = 'Active System Administrators';
    } else {
      filtered = this.immutableTableData.filter(u => !u.isApproved);
      this.pageTitle = 'Deactivated System Administrators';
    }

    this.tableDataSource.data = filtered;
    this.sortData(this.currentSort);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.tableDataSource.filter = filterValue.trim().toLowerCase();
  }

  private endpointFor(user: SystemAdminUser): string {
    return user.accountType === 'SuperAdmin'
      ? `${this.backendApi}/api/SuperAdmin/users/${user.id}`
      : `${this.backendApi}/api/Admin/global-event-managers/${user.id}`;
  }

  viewDetails(user: SystemAdminUser) {
    const ref = this.dialog.open(SuperAdminDetailsComponent, {
      maxHeight: '93vh',
      maxWidth: '95vw',
      width: '800px',
      data: user
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.updated) {
        this.loadAllUsers();
      }
    });
  }

  deactivateUser(user: SystemAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: `Deactivate ${user.accountType === 'SuperAdmin' ? 'SuperAdmin' : 'Event Manager'}`,
      message: `Are you sure you want to deactivate this user? They will lose access to the system.`,
      actionType: 'archive',
      itemName: user.fullName,
      requiresReason: true,
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px', data: dialogData, disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performDeactivate(user, result.reason || 'No reason provided');
      }
    });
  }

  private performDeactivate(user: SystemAdminUser, reason: string) {
    this.http.post(`${this.endpointFor(user)}/deactivate`, { reason })
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'User deactivated successfully' }, duration: 3000,
          });
          this.loadAllUsers();
        },
        error: (err) => {
          console.error('Deactivate error:', err);
          this.errorService.openandCloseError(err.error?.message || 'Failed to deactivate user');
        }
      });
  }

  reactivateUser(user: SystemAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: `Reactivate ${user.accountType === 'SuperAdmin' ? 'SuperAdmin' : 'Event Manager'}`,
      message: `Are you sure you want to reactivate this user? They will regain access.`,
      actionType: 'reactivate',
      itemName: user.fullName,
      requiresReason: false,
      confirmButtonText: 'Reactivate',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px', data: dialogData, disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performReactivate(user);
      }
    });
  }

  private performReactivate(user: SystemAdminUser) {
    this.http.post(`${this.endpointFor(user)}/reactivate`, {})
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'User reactivated successfully' }, duration: 3000,
          });
          this.loadAllUsers();
        },
        error: (err) => {
          console.error('Reactivate error:', err);
          this.errorService.openandCloseError(err.error?.message || 'Failed to reactivate user');
        }
      });
  }

  deleteUser(user: SystemAdminUser) {
    const isEventManager = user.accountType === 'EventManager';
    const dialogData: ConfirmActionDialogData = {
      title: `Delete ${isEventManager ? 'Event Manager' : 'SuperAdmin'}`,
      message: isEventManager
        ? `This will revoke the Global Event Manager role from this user. Are you sure?`
        : `⚠️ WARNING: This action is PERMANENT and cannot be undone. Are you absolutely sure?`,
      actionType: 'delete',
      itemName: user.fullName,
      requiresReason: !isEventManager,
      confirmButtonText: isEventManager ? 'Revoke Role' : 'Delete Permanently',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px', data: dialogData, disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performDelete(user, result.reason || 'No reason provided');
      }
    });
  }

  private performDelete(user: SystemAdminUser, reason: string) {
    const isEventManager = user.accountType === 'EventManager';
    const request$ = isEventManager
      ? this.http.delete(this.endpointFor(user)) // revoke role, no body needed
      : this.http.delete(this.endpointFor(user), { body: { reason } });

    request$.subscribe({
      next: () => {
        this._sn.openFromComponent(SuccessMessageComponent, {
          data: { message: isEventManager ? 'Event Manager role revoked' : 'SuperAdmin user deleted permanently' },
          duration: 3000,
        });
        this.loadAllUsers();
      },
      error: (err) => {
        console.error('Delete error:', err);
        this.errorService.openandCloseError(err.error?.message || 'Failed to delete user');
      }
    });
  }

  resetPassword(user: SystemAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: 'Reset Password',
      message: `Reset password for ${user.fullName}? A new temporary password will be sent to ${user.email}.`,
      actionType: 'reset',
      itemName: user.fullName,
      requiresReason: false,
      confirmButtonText: 'Reset Password',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px', data: dialogData, disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performPasswordReset(user);
      }
    });
  }

  private performPasswordReset(user: SystemAdminUser) {
    this.http.post(`${this.endpointFor(user)}/reset-password`, {})
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Password reset successfully. New password sent via email.' }, duration: 3000,
          });
          this.loadAllUsers();
        },
        error: (err) => {
          console.error('Password reset error:', err);
          this.errorService.openandCloseError(err.error?.message || 'Failed to reset password');
        }
      });
  }

  navigateToCreate() {
    this.router.navigate(['/app/superadmin/create']);
  }
}